
import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Users, Settings, Shield, Activity, Search, Edit, Lock, CheckCircle, XCircle, Save, Plus, AlertTriangle, Loader2,
  Database, Trash2, Upload, X, Image, PenTool, ToggleLeft, ToggleRight, Copy, KeyRound,
} from 'lucide-react';
import { Button, Input, Card, Badge, Modal, ConfirmationDialog, FieldHint, InfoTooltip, useToast } from './ui';
import { Role, User } from '../types';
import { apiClient, adminService } from '../src/services';
import type { CompanySettings as CompanySettingsType, ProductTypeRef, ProductTypeCreate } from '../src/services/adminService';

// Transform API user to local format
const transformUser = (u: any): User => ({
  id: u.id,
  name: u.full_name || u.email,
  email: u.email,
  role: u.role || Role.COMMERCIAL,
  status: u.is_active ? 'ACTIVE' : 'INACTIVE',
  lastLogin: u.last_login || 'Jamais',
  avatar: (u.full_name || u.email || '').substring(0, 2).toUpperCase(),
});

// --- Users Tab ---

const UsersTab = () => {
  const [filter, setFilter] = useState('');
  const [users, setUsers] = useState<User[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingUser, setEditingUser] = useState<Partial<User>>({});
  const [isSaving, setIsSaving] = useState(false);
  const [resetTarget, setResetTarget] = useState<User | null>(null);
  const { toast } = useToast();

  // Fetch users from API
  const fetchUsers = useCallback(async () => {
    setIsLoading(true);
    try {
      const response = await apiClient.get('/users', { params: { page_size: 100 } });
      const body = response.data;
      const usersData = Array.isArray(body?.data) ? body.data : (body?.data?.items || []);
      setUsers(usersData.map(transformUser));
    } catch (error) {
      console.error('Error fetching users:', error);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchUsers();
  }, [fetchUsers]);

  const filteredUsers = users.filter(u =>
    u.name.toLowerCase().includes(filter.toLowerCase()) ||
    u.email.toLowerCase().includes(filter.toLowerCase())
  );

  const handleEdit = (user: User) => {
    setEditingUser(user);
    setIsModalOpen(true);
  };

  const handleCreate = () => {
    setEditingUser({ status: 'ACTIVE', role: Role.COMMERCIAL });
    setIsModalOpen(true);
  };

  const handleSave = async () => {
    // Basic validation
    if (!editingUser.name?.trim() || !editingUser.email?.trim()) {
      toast('Le nom et l\'email sont obligatoires', 'error');
      return;
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(editingUser.email)) {
      toast('L\'adresse email n\'est pas valide', 'error');
      return;
    }
    setIsSaving(true);
    try {
      if (editingUser.id) {
        // Update existing user
        await apiClient.patch(`/users/${editingUser.id}`, {
          full_name: editingUser.name,
          email: editingUser.email,
          role: editingUser.role,
          is_active: editingUser.status === 'ACTIVE',
        });
        toast('Utilisateur modifié avec succès', 'success');
      } else {
        // Create new user — backend generates the temp password and emails it
        await apiClient.post('/users', {
          full_name: editingUser.name,
          email: editingUser.email,
          role: editingUser.role,
        });
        toast(`Utilisateur créé. Le mot de passe temporaire a été envoyé à ${editingUser.email}.`, 'success');
      }
      setIsModalOpen(false);
      fetchUsers(); // Refresh list
    } catch (error: any) {
      console.error('Error saving user:', error);
      toast('Erreur: ' + (error?.message || 'Impossible de sauvegarder'), 'error');
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className="space-y-6 animate-in fade-in">
      <div className="flex justify-between items-center">
        <div className="relative w-64">
           <Search className="absolute left-2 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
           <input
             className="pl-8 w-full border-slate-300 rounded-md text-sm py-2"
             placeholder="Rechercher utilisateur..."
             value={filter}
             onChange={e => setFilter(e.target.value)}
           />
        </div>
        <Button icon={Plus} onClick={handleCreate}>Nouvel Utilisateur</Button>
      </div>

      <div className="bg-white border border-slate-200 rounded-lg overflow-hidden">
        {isLoading ? (
          <div className="flex justify-center py-12">
            <Loader2 className="w-8 h-8 animate-spin text-primary-500" />
          </div>
        ) : (
        <table className="min-w-full divide-y divide-slate-200">
           <thead className="bg-slate-50">
             <tr>
               <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase">Utilisateur</th>
               <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase">Rôle</th>
               <th className="px-6 py-3 text-center text-xs font-medium text-slate-500 uppercase">Statut</th>
               <th className="px-6 py-3 text-right text-xs font-medium text-slate-500 uppercase">Dernière Connexion</th>
               <th className="px-6 py-3 text-right text-xs font-medium text-slate-500 uppercase">Actions</th>
             </tr>
           </thead>
           <tbody className="divide-y divide-slate-200">
             {filteredUsers.length === 0 ? (
               <tr><td colSpan={5} className="text-center py-8 text-slate-500">Aucun utilisateur trouvé</td></tr>
             ) : filteredUsers.map(u => (
               <tr key={u.id} className="hover:bg-slate-50">
                 <td className="px-6 py-4">
                    <div className="flex items-center">
                       <div className="h-8 w-8 rounded-full bg-slate-200 flex items-center justify-center text-xs font-bold text-slate-600 mr-3">
                          {u.avatar}
                       </div>
                       <div>
                          <div className="text-sm font-medium text-slate-900">{u.name}</div>
                          <div className="text-xs text-slate-500">{u.email}</div>
                       </div>
                    </div>
                 </td>
                 <td className="px-6 py-4 text-sm text-slate-600">
                    <Badge color="blue">{u.role}</Badge>
                 </td>
                 <td className="px-6 py-4 text-center">
                    {u.status === 'ACTIVE' ? (
                      <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-green-100 text-green-800">
                         <CheckCircle className="w-3 h-3 mr-1" /> Actif
                      </span>
                    ) : (
                      <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-gray-100 text-gray-800">
                         <XCircle className="w-3 h-3 mr-1" /> Inactif
                      </span>
                    )}
                 </td>
                 <td className="px-6 py-4 text-right text-sm text-slate-500">{u.lastLogin}</td>
                 <td className="px-6 py-4 text-right">
                    <button onClick={() => setResetTarget(u)} title="Réinitialiser le mot de passe" className="text-slate-400 hover:text-orange-600 mr-3"><KeyRound className="w-4 h-4" /></button>
                    <button onClick={() => handleEdit(u)} title="Modifier" className="text-slate-400 hover:text-blue-600"><Edit className="w-4 h-4" /></button>
                 </td>
               </tr>
             ))}
           </tbody>
        </table>
        )}
      </div>

      {isModalOpen && (
        <Modal isOpen={true} onClose={() => setIsModalOpen(false)} title={editingUser.id ? "Modifier Utilisateur" : "Créer Utilisateur"}>
           <div className="space-y-4">
              <Input label="Nom Complet" required hint="Prénom et Nom — apparaît dans les journaux d'audit" value={editingUser.name || ''} onChange={e => setEditingUser({...editingUser, name: e.target.value})} />
              <Input label="Email" type="email" required hint="Utilisé pour l'identifiant de connexion" value={editingUser.email || ''} onChange={e => setEditingUser({...editingUser, email: e.target.value})} />
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Rôle <span className="text-red-500 ml-0.5">*</span></label>
                <select
                  className="w-full border-slate-300 rounded-md py-2"
                  value={editingUser.role}
                  onChange={e => setEditingUser({...editingUser, role: e.target.value as Role})}
                >
                   {Object.values(Role).map(r => <option key={r} value={r}>{r}</option>)}
                </select>
                <FieldHint>Détermine les accès aux modules et les permissions</FieldHint>
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Statut <span className="text-red-500 ml-0.5">*</span></label>
                <select
                  className="w-full border-slate-300 rounded-md py-2"
                  value={editingUser.status}
                  onChange={e => setEditingUser({...editingUser, status: e.target.value as any})}
                >
                   <option value="ACTIVE">Actif</option>
                   <option value="INACTIVE">Inactif</option>
                </select>
                <FieldHint>Un utilisateur inactif ne peut plus se connecter</FieldHint>
              </div>
              <div className="flex justify-end gap-3 pt-4">
                 <Button variant="ghost" onClick={() => setIsModalOpen(false)} disabled={isSaving}>Annuler</Button>
                 <Button onClick={handleSave} disabled={isSaving}>
                   {isSaving ? (
                     <>
                       <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                       Enregistrement...
                     </>
                   ) : 'Enregistrer'}
                 </Button>
              </div>
           </div>
        </Modal>
      )}

      <ConfirmationDialog
        isOpen={!!resetTarget}
        onClose={() => setResetTarget(null)}
        onConfirm={async () => {
          if (!resetTarget) return;
          try {
            await apiClient.post(`/users/${resetTarget.id}/reset-password`);
            toast(`Mot de passe réinitialisé. Un email a été envoyé à ${resetTarget.email}.`, 'success');
          } catch (error: any) {
            toast('Erreur: ' + (error?.message || 'Impossible de réinitialiser'), 'error');
            throw error;
          }
        }}
        title="Réinitialiser le mot de passe"
        message={resetTarget ? `Un nouveau mot de passe temporaire sera généré et envoyé par email à ${resetTarget.email}. L'utilisateur devra le changer à sa prochaine connexion.` : ''}
        confirmLabel="Réinitialiser"
        variant="warning"
      />
    </div>
  );
};

// --- Roles Tab ---

const MODULES = ['STOCK', 'VENTES', 'LOCATIONS', 'CLIENTS', 'REPORTING', 'ADMIN'] as const;
const MODULE_LABELS: Record<string, string> = {
  STOCK: 'Stock', VENTES: 'Ventes', LOCATIONS: 'Locations',
  CLIENTS: 'Clients', REPORTING: 'Reporting', ADMIN: 'Admin',
};
const LEVEL_CYCLE = ['RW', 'R', 'NONE'] as const;

const RolesTab = () => {
  const [matrix, setMatrix] = useState<Record<string, Record<string, string>>>({});
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [isDirty, setIsDirty] = useState(false);
  const { toast } = useToast();

  useEffect(() => {
    const fetchMatrix = async () => {
      setIsLoading(true);
      try {
        const response = await apiClient.get('/admin/permissions');
        setMatrix(response.data?.data || response.data || {});
      } catch (error) {
        console.error('Error fetching permissions:', error);
        toast('Erreur lors du chargement des permissions', 'error');
      } finally {
        setIsLoading(false);
      }
    };
    fetchMatrix();
  }, []);

  const cycleLevel = (role: string, mod: string) => {
    if (role === Role.ADMIN) return; // ADMIN always RW
    const current = matrix[role]?.[mod] || 'NONE';
    const idx = LEVEL_CYCLE.indexOf(current as any);
    const next = LEVEL_CYCLE[(idx + 1) % LEVEL_CYCLE.length];
    setMatrix(prev => ({
      ...prev,
      [role]: { ...(prev[role] || {}), [mod]: next },
    }));
    setIsDirty(true);
  };

  const handleSave = async () => {
    setIsSaving(true);
    try {
      const permissions: { role: string; module: string; level: string }[] = [];
      for (const role of Object.keys(matrix)) {
        if (role === Role.ADMIN) continue;
        for (const mod of MODULES) {
          permissions.push({ role, module: mod, level: matrix[role]?.[mod] || 'NONE' });
        }
      }
      const response = await apiClient.put('/admin/permissions', { permissions });
      setMatrix(response.data?.data || response.data || {});
      setIsDirty(false);
      toast('Permissions enregistrées avec succès', 'success');
    } catch (error) {
      console.error('Error saving permissions:', error);
      toast('Erreur lors de la sauvegarde des permissions', 'error');
    } finally {
      setIsSaving(false);
    }
  };

  if (isLoading) {
    return (
      <div className="flex justify-center py-12">
        <Loader2 className="w-8 h-8 animate-spin text-primary-500" />
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-in fade-in">
       <div className="bg-blue-50 p-4 rounded-lg border border-blue-100 flex items-start gap-3">
          <Shield className="w-5 h-5 text-blue-600 mt-0.5" />
          <div className="text-sm text-blue-800">
             <p className="font-bold">Matrice des Permissions</p>
             <p>Cliquez sur une cellule pour modifier la permission. Les changements ne sont appliqués qu'après enregistrement.</p>
          </div>
       </div>

       <div className="bg-white border border-slate-200 rounded-lg overflow-hidden">
          <table className="min-w-full divide-y divide-slate-200">
             <thead className="bg-slate-50">
                <tr>
                   <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase">Module</th>
                   {Object.values(Role).map(r => (
                      <th key={r} className="px-6 py-3 text-center text-xs font-medium text-slate-500 uppercase">{r}</th>
                   ))}
                </tr>
             </thead>
             <tbody className="divide-y divide-slate-200">
                {MODULES.map((mod) => (
                   <tr key={mod} className="hover:bg-slate-50">
                      <td className="px-6 py-4 text-sm font-bold text-slate-700">{MODULE_LABELS[mod]}</td>
                      {Object.values(Role).map(r => {
                         const perm = matrix[r]?.[mod] || 'NONE';
                         const isAdmin = r === Role.ADMIN;
                         let badgeColor: any = 'gray';
                         if (perm === 'RW') badgeColor = 'green';
                         if (perm === 'R') badgeColor = 'blue';

                         return (
                            <td key={r} className="px-6 py-4 text-center">
                               <button
                                 onClick={() => cycleLevel(r, mod)}
                                 disabled={isAdmin}
                                 className={`inline-block ${isAdmin ? 'cursor-not-allowed opacity-70' : 'cursor-pointer hover:scale-105 transition-transform'}`}
                                 title={isAdmin ? 'ADMIN: toujours RW' : 'Cliquez pour modifier'}
                               >
                                 <Badge color={badgeColor}>
                                   {perm === 'RW' ? 'Lecture/Écriture' : perm === 'R' ? 'Lecture Seule' : 'Accès Interdit'}
                                 </Badge>
                               </button>
                            </td>
                         );
                      })}
                   </tr>
                ))}
             </tbody>
          </table>
       </div>

       {isDirty && (
         <div className="flex justify-end">
           <Button icon={Save} onClick={handleSave} loading={isSaving}>
             Enregistrer les Permissions
           </Button>
         </div>
       )}
    </div>
  );
};

// --- Settings Tab (API-backed) ---

const SettingsTab = () => {
  const [settings, setSettings] = useState<CompanySettingsType | null>(null);
  const [form, setForm] = useState<Record<string, any>>({});
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const [uploadingSig, setUploadingSig] = useState(false);
  const logoInputRef = useRef<HTMLInputElement>(null);
  const sigInputRef = useRef<HTMLInputElement>(null);
  const { toast } = useToast();

  const fetchSettings = useCallback(async () => {
    setIsLoading(true);
    try {
      const data = await adminService.getSettings();
      setSettings(data);
      setForm(data);
    } catch (error) {
      console.error('Error fetching settings:', error);
      toast('Erreur lors du chargement des paramètres', 'error');
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => { fetchSettings(); }, [fetchSettings]);

  const handleSave = async () => {
    setIsSaving(true);
    try {
      const { id, logo_url, signature_url, created_at, updated_at, ...updateData } = form;
      const data = await adminService.updateSettings(updateData);
      setSettings(data);
      setForm(data);
      toast('Paramètres enregistrés avec succès', 'success');
    } catch (error: any) {
      console.error('Error saving settings:', error);
      toast('Erreur: ' + (error?.message || 'Impossible de sauvegarder'), 'error');
    } finally {
      setIsSaving(false);
    }
  };

  const handleLogoUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploadingLogo(true);
    try {
      const data = await adminService.uploadLogo(file);
      setSettings(data);
      setForm(data);
      toast('Logo uploadé avec succès', 'success');
    } catch (error: any) {
      toast('Erreur upload logo: ' + (error?.message || 'Échec'), 'error');
    } finally {
      setUploadingLogo(false);
      if (logoInputRef.current) logoInputRef.current.value = '';
    }
  };

  const handleSigUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploadingSig(true);
    try {
      const data = await adminService.uploadSignature(file);
      setSettings(data);
      setForm(data);
      toast('Signature uploadée avec succès', 'success');
    } catch (error: any) {
      toast('Erreur upload signature: ' + (error?.message || 'Échec'), 'error');
    } finally {
      setUploadingSig(false);
      if (sigInputRef.current) sigInputRef.current.value = '';
    }
  };

  const handleDeleteLogo = async () => {
    try {
      const data = await adminService.deleteLogo();
      setSettings(data);
      setForm(data);
      toast('Logo supprimé', 'success');
    } catch (error: any) {
      toast('Erreur: ' + (error?.message || 'Échec'), 'error');
    }
  };

  const handleDeleteSig = async () => {
    try {
      const data = await adminService.deleteSignature();
      setSettings(data);
      setForm(data);
      toast('Signature supprimée', 'success');
    } catch (error: any) {
      toast('Erreur: ' + (error?.message || 'Échec'), 'error');
    }
  };

  const updateField = (key: string, value: any) => {
    setForm(prev => ({ ...prev, [key]: value }));
  };

  if (isLoading || !settings) {
    return (
      <div className="flex justify-center py-12">
        <Loader2 className="w-8 h-8 animate-spin text-primary-500" />
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-in fade-in max-w-5xl">
       {/* Section 1: Informations Société */}
       <Card title="Informations Société">
          <div className="grid grid-cols-2 gap-4">
             <Input label="Raison Sociale" required value={form.raison_sociale || ''} onChange={e => updateField('raison_sociale', e.target.value)} />
             <Input label="Forme Juridique" value={form.forme_juridique || ''} onChange={e => updateField('forme_juridique', e.target.value)} placeholder="ex: SARL" />
             <Input label="Capital Social" value={form.capital_social || ''} onChange={e => updateField('capital_social', e.target.value)} placeholder="ex: 10 000 000 FCFA" />
             <Input label="Adresse" required value={form.adresse || ''} onChange={e => updateField('adresse', e.target.value)} />
             <Input label="Ville" value={form.ville || ''} onChange={e => updateField('ville', e.target.value)} />
             <Input label="Pays" value={form.pays || ''} onChange={e => updateField('pays', e.target.value)} />
             <Input label="Téléphone" required value={form.telephone || ''} onChange={e => updateField('telephone', e.target.value)} />
             <Input label="Email" type="email" required value={form.email || ''} onChange={e => updateField('email', e.target.value)} />
             <Input label="Site Web" value={form.site_web || ''} onChange={e => updateField('site_web', e.target.value)} placeholder="https://..." />
             <Input label="IFU" required value={form.ifu || ''} onChange={e => updateField('ifu', e.target.value)} />
             <Input label="RCCM" required value={form.rccm || ''} onChange={e => updateField('rccm', e.target.value)} />
          </div>
       </Card>

       {/* Section 2: Logo & Signature */}
       <Card title="Logo & Signature">
          <div className="grid grid-cols-2 gap-6">
             <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">Logo de l'entreprise</label>
                {settings.logo_url ? (
                  <div className="flex items-center gap-4">
                    <img src={settings.logo_url} alt="Logo" className="h-16 object-contain border rounded p-1" />
                    <button onClick={handleDeleteLogo} className="text-red-500 hover:text-red-700" title="Supprimer le logo">
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                ) : (
                  <div className="text-sm text-slate-400 mb-2">Aucun logo</div>
                )}
                <input ref={logoInputRef} type="file" accept="image/*" className="hidden" onChange={handleLogoUpload} />
                <Button variant="ghost" size="sm" icon={uploadingLogo ? Loader2 : Upload} onClick={() => logoInputRef.current?.click()} disabled={uploadingLogo} className="mt-2">
                  {uploadingLogo ? 'Upload...' : 'Charger un logo'}
                </Button>
             </div>
             <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">Signature (pour documents)</label>
                {settings.signature_url ? (
                  <div className="flex items-center gap-4">
                    <img src={settings.signature_url} alt="Signature" className="h-16 object-contain border rounded p-1" />
                    <button onClick={handleDeleteSig} className="text-red-500 hover:text-red-700" title="Supprimer la signature">
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                ) : (
                  <div className="text-sm text-slate-400 mb-2">Aucune signature</div>
                )}
                <input ref={sigInputRef} type="file" accept="image/*" className="hidden" onChange={handleSigUpload} />
                <Button variant="ghost" size="sm" icon={uploadingSig ? Loader2 : PenTool} onClick={() => sigInputRef.current?.click()} disabled={uploadingSig} className="mt-2">
                  {uploadingSig ? 'Upload...' : 'Charger une signature'}
                </Button>
             </div>
          </div>
       </Card>

       {/* Section 3: Paramètres Métier */}
       <Card title="Paramètres Métier">
          <div className="grid grid-cols-2 gap-4">
             <Input
               label="TVA (%)" type="number" min={0} max={100}
               tooltip="Taux de TVA appliqué par défaut sur les devis et factures"
               value={form.tva_default ?? 18}
               onChange={e => updateField('tva_default', parseInt(e.target.value) || 0)}
             />
             <Input
               label="Caution Minimum (%)" type="number" min={0} max={100}
               tooltip="Pourcentage minimum exigé avant validation d'une livraison"
               value={form.min_deposit_percent ?? 30}
               onChange={e => updateField('min_deposit_percent', parseInt(e.target.value) || 0)}
             />
             <Input
               label="Devise" disabled
               tooltip="Devise utilisée dans tout le système"
               value={form.currency || 'XOF'}
             />
             <Input
               label="Seuil Alerte Stock" type="number" min={0}
               tooltip="Seuil par défaut sous lequel une alerte de stock bas est déclenchée"
               value={form.low_stock_threshold ?? 5}
               onChange={e => updateField('low_stock_threshold', parseInt(e.target.value) || 0)}
             />
             <Input
               label="Plafond Dommages Caution (XOF)" type="number" min={0}
               tooltip="Montant maximum estimé pour les dommages retenus sur la caution lors du retour de machine"
               value={form.plafond_dommages_caution ?? 50000}
               onChange={e => updateField('plafond_dommages_caution', parseInt(e.target.value) || 0)}
             />
          </div>
       </Card>

       {/* Section 4: Paramètres du Workflow */}
       <Card title="Paramètres du Workflow">
          <p className="text-sm text-slate-500 mb-4">Configurez le comportement des workflows de vente, location et LOA.</p>
          <div className="space-y-4">
             <div className="flex items-center justify-between py-2 border-b border-slate-100">
                <div>
                   <div className="text-sm font-medium text-slate-700">Générer BL automatiquement après paiement</div>
                   <div className="text-xs text-slate-500">Crée un bon de livraison automatique quand une facture VENTE est payée</div>
                </div>
                <button onClick={() => updateField('auto_generate_bl_on_payment', !form.auto_generate_bl_on_payment)} className="flex-shrink-0">
                   {form.auto_generate_bl_on_payment ? <ToggleRight className="w-8 h-8 text-green-500" /> : <ToggleLeft className="w-8 h-8 text-slate-400" />}
                </button>
             </div>
             <div className="flex items-center justify-between py-2 border-b border-slate-100">
                <div>
                   <div className="text-sm font-medium text-slate-700">Validation GÉRANT requise (Location/LOA)</div>
                   <div className="text-xs text-slate-500">Le gérant doit approuver les contrats avant génération du BL</div>
                </div>
                <button onClick={() => updateField('require_gerant_validation', !form.require_gerant_validation)} className="flex-shrink-0">
                   {form.require_gerant_validation ? <ToggleRight className="w-8 h-8 text-green-500" /> : <ToggleLeft className="w-8 h-8 text-slate-400" />}
                </button>
             </div>
             <div className="flex items-center justify-between py-2 border-b border-slate-100">
                <div>
                   <div className="text-sm font-medium text-slate-700">État des lieux d'entrée obligatoire</div>
                   <div className="text-xs text-slate-500">Exige un état des lieux avant la livraison de la machine</div>
                </div>
                <button onClick={() => updateField('require_entry_condition_report', !form.require_entry_condition_report)} className="flex-shrink-0">
                   {form.require_entry_condition_report ? <ToggleRight className="w-8 h-8 text-green-500" /> : <ToggleLeft className="w-8 h-8 text-slate-400" />}
                </button>
             </div>
             <div className="flex items-center justify-between py-2">
                <div>
                   <div className="text-sm font-medium text-slate-700">État des lieux de sortie obligatoire</div>
                   <div className="text-xs text-slate-500">Exige un état des lieux avant la clôture du contrat</div>
                </div>
                <button onClick={() => updateField('require_exit_condition_report', !form.require_exit_condition_report)} className="flex-shrink-0">
                   {form.require_exit_condition_report ? <ToggleRight className="w-8 h-8 text-green-500" /> : <ToggleLeft className="w-8 h-8 text-slate-400" />}
                </button>
             </div>
          </div>
       </Card>

       {/* Section 5: Modes de Paiement */}
       <Card title="Modes de Paiement">
          <p className="text-sm text-slate-500 mb-4">Activez ou désactivez les modes de paiement et personnalisez leurs libellés.</p>
          <div className="space-y-3">
            {(form.modes_paiement || []).map((m: { code: string; label: string; active: boolean }, idx: number) => (
              <div key={m.code} className="flex items-center gap-3 py-2 border-b border-slate-100">
                <button
                  onClick={() => {
                    const updated = [...(form.modes_paiement || [])];
                    updated[idx] = { ...updated[idx], active: !updated[idx].active };
                    updateField('modes_paiement', updated);
                  }}
                  className="flex-shrink-0"
                >
                  {m.active ? <ToggleRight className="w-8 h-8 text-green-500" /> : <ToggleLeft className="w-8 h-8 text-slate-400" />}
                </button>
                <div className="flex-1 flex items-center gap-3">
                  <span className="text-xs text-slate-400 font-mono w-32 flex-shrink-0">{m.code}</span>
                  <Input
                    value={m.label}
                    onChange={e => {
                      const updated = [...(form.modes_paiement || [])];
                      updated[idx] = { ...updated[idx], label: e.target.value };
                      updateField('modes_paiement', updated);
                    }}
                    className="flex-1"
                  />
                </div>
              </div>
            ))}
            {(!form.modes_paiement || form.modes_paiement.length === 0) && (
              <p className="text-sm text-slate-400 italic">Aucun mode configuré. Sauvegardez pour initialiser les modes par défaut.</p>
            )}
          </div>
       </Card>

       {/* Section 6: Préfixes Documents */}
       <Card title="Préfixes Documents">
          <div className="grid grid-cols-3 gap-4">
             <Input label="Facture" value={form.prefix_invoice || ''} onChange={e => updateField('prefix_invoice', e.target.value)} />
             <Input label="Devis" value={form.prefix_quote || ''} onChange={e => updateField('prefix_quote', e.target.value)} />
             <Input label="Contrat" value={form.prefix_contract || ''} onChange={e => updateField('prefix_contract', e.target.value)} />
             <Input label="LOA" value={form.prefix_loa || ''} onChange={e => updateField('prefix_loa', e.target.value)} />
             <Input label="Bon de Livraison" value={form.prefix_delivery || ''} onChange={e => updateField('prefix_delivery', e.target.value)} />
             <Input label="Mouvement" value={form.prefix_movement || ''} onChange={e => updateField('prefix_movement', e.target.value)} />
             <Input label="Client" value={form.prefix_client || ''} onChange={e => updateField('prefix_client', e.target.value)} />
          </div>
       </Card>

       {/* Section 6: Mentions Légales */}
       <Card title="Mentions Légales">
          <div>
            <textarea
              className="w-full border-slate-300 rounded-md shadow-sm text-sm min-h-[100px] p-3"
              placeholder="Mentions légales affichées sur les documents PDF..."
              value={form.mentions_legales || ''}
              onChange={e => updateField('mentions_legales', e.target.value)}
            />
            <FieldHint>Ces mentions apparaîtront en bas des factures et devis générés</FieldHint>
          </div>
       </Card>

       <div className="flex justify-end">
          <Button icon={Save} onClick={handleSave} disabled={isSaving}>
            {isSaving ? 'Sauvegarde...' : 'Enregistrer les Modifications'}
          </Button>
       </div>
    </div>
  );
};

// --- Référentiel Tab ---

interface ReferenceItem {
  id: string;
  libelle?: string;
  code?: string;
  nom?: string;
  description?: string;
  is_active: boolean;
  [key: string]: any;
}

interface ColumnDef {
  key: string;
  label: string;
  type?: 'text' | 'select' | 'boolean';
  options?: { value: string; label: string }[];
  required?: boolean;
  editable?: boolean;
}

interface ReferenceDataTableProps {
  title: string;
  endpoint: string;
  columns: ColumnDef[];
  nameField?: string; // field used as display name (libelle, nom, code...)
  fetchParams?: Record<string, any>; // extra query params for GET
}

const BASE_TYPE_OPTIONS = [
  { value: 'MACHINE', label: 'Machine' },
  { value: 'PART', label: 'Pièce' },
  { value: 'CONSUMABLE', label: 'Consommable' },
];

const ReferenceDataTable = ({ title, endpoint, columns, nameField = 'libelle', fetchParams }: ReferenceDataTableProps) => {
  const [items, setItems] = useState<ReferenceItem[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editingData, setEditingData] = useState<Record<string, any>>({});
  const [isAdding, setIsAdding] = useState(false);
  const [newItem, setNewItem] = useState<Record<string, any>>({});
  const [savingId, setSavingId] = useState<string | null>(null);
  const [deleteConfirmItem, setDeleteConfirmItem] = useState<ReferenceItem | null>(null);
  const { toast } = useToast();

  const fetchItems = useCallback(async () => {
    setIsLoading(true);
    try {
      const response = await apiClient.get(endpoint, fetchParams ? { params: fetchParams } : undefined);
      const body = response.data;
      const data = Array.isArray(body?.data) ? body.data : (body?.data?.items || []);
      setItems(data);
    } catch (error: any) {
      console.error(`Error fetching ${title}:`, error);
      toast(`Erreur chargement ${title}: ${error?.message || 'Erreur inconnue'}`, 'error');
    } finally {
      setIsLoading(false);
    }
  }, [endpoint, title, fetchParams]);

  useEffect(() => { fetchItems(); }, [fetchItems]);

  const filteredItems = items.filter(item => {
    const name = item[nameField] || item.libelle || item.nom || '';
    return name.toLowerCase().includes(search.toLowerCase());
  });

  const startEdit = (item: ReferenceItem) => {
    setEditingId(item.id);
    setEditingData({ ...item });
  };

  const cancelEdit = () => {
    setEditingId(null);
    setEditingData({});
  };

  const saveEdit = async () => {
    if (!editingId) return;
    setSavingId(editingId);
    try {
      const { id, created_at, updated_at, is_active, is_default, ...data } = editingData;
      await apiClient.patch(`${endpoint}/${editingId}`, data);
      toast(`${title} modifié`, 'success');
      setEditingId(null);
      fetchItems();
    } catch (error: any) {
      toast('Erreur: ' + (error?.message || 'Échec'), 'error');
    } finally {
      setSavingId(null);
    }
  };

  const addItem = async () => {
    setSavingId('new');
    try {
      await apiClient.post(endpoint, newItem);
      toast(`${title} ajouté`, 'success');
      setIsAdding(false);
      setNewItem({});
      fetchItems();
    } catch (error: any) {
      toast('Erreur: ' + (error?.message || 'Échec'), 'error');
    } finally {
      setSavingId(null);
    }
  };

  const toggleActive = async (item: ReferenceItem) => {
    try {
      await apiClient.patch(`${endpoint}/${item.id}`, { is_active: !item.is_active });
      fetchItems();
    } catch (error: any) {
      toast('Erreur: ' + (error?.message || 'Échec'), 'error');
    }
  };

  const handleDeleteConfirm = async () => {
    if (!deleteConfirmItem) return;
    try {
      await apiClient.delete(`${endpoint}/${deleteConfirmItem.id}`);
      toast(`${title} supprimé`, 'success');
      setDeleteConfirmItem(null);
      fetchItems();
    } catch (error: any) {
      toast('Erreur: ' + (error?.message || 'Impossible de supprimer'), 'error');
    }
  };

  if (isLoading) {
    return (
      <div className="flex justify-center py-8">
        <Loader2 className="w-6 h-6 animate-spin text-primary-500" />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <div className="relative w-64">
          <Search className="absolute left-2 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
          <input
            className="pl-8 w-full border-slate-300 rounded-md text-sm py-2"
            placeholder="Rechercher..."
            value={search} onChange={e => setSearch(e.target.value)}
          />
        </div>
        <Button icon={Plus} size="sm" onClick={() => { setIsAdding(true); setNewItem({ is_active: true }); }}>
          Ajouter
        </Button>
      </div>

      <div className="bg-white border border-slate-200 rounded-lg overflow-hidden">
        <table className="min-w-full divide-y divide-slate-200">
          <thead className="bg-slate-50">
            <tr>
              {columns.map(col => (
                <th key={col.key} className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase">{col.label}</th>
              ))}
              <th className="px-4 py-3 text-center text-xs font-medium text-slate-500 uppercase">Actif</th>
              <th className="px-4 py-3 text-right text-xs font-medium text-slate-500 uppercase">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-200">
            {/* Add new row */}
            {isAdding && (
              <tr className="bg-blue-50">
                {columns.map(col => (
                  <td key={col.key} className="px-4 py-2">
                    {col.type === 'select' ? (
                      <select
                        className="w-full border-slate-300 rounded text-sm py-1"
                        value={newItem[col.key] || ''}
                        onChange={e => setNewItem({ ...newItem, [col.key]: e.target.value })}
                      >
                        <option value="">--</option>
                        {col.options?.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                      </select>
                    ) : col.type === 'boolean' ? (
                      <input type="checkbox" checked={!!newItem[col.key]} onChange={e => setNewItem({ ...newItem, [col.key]: e.target.checked })} />
                    ) : (
                      <input
                        className="w-full border-slate-300 rounded text-sm py-1 px-2"
                        placeholder={col.label}
                        value={newItem[col.key] || ''}
                        onChange={e => setNewItem({ ...newItem, [col.key]: e.target.value })}
                      />
                    )}
                  </td>
                ))}
                <td className="px-4 py-2 text-center">
                  <input type="checkbox" checked={newItem.is_active !== false} onChange={e => setNewItem({ ...newItem, is_active: e.target.checked })} />
                </td>
                <td className="px-4 py-2 text-right space-x-2">
                  <button onClick={addItem} disabled={savingId === 'new' || !columns.filter(col => col.required).every(col => newItem[col.key]?.toString().trim())} className="text-green-600 hover:text-green-800 disabled:opacity-50 disabled:cursor-not-allowed">
                    {savingId === 'new' ? <Loader2 className="w-4 h-4 animate-spin inline" /> : <CheckCircle className="w-4 h-4 inline" />}
                  </button>
                  <button onClick={() => { setIsAdding(false); setNewItem({}); }} className="text-slate-400 hover:text-slate-600">
                    <X className="w-4 h-4 inline" />
                  </button>
                </td>
              </tr>
            )}

            {filteredItems.length === 0 && !isAdding ? (
              <tr><td colSpan={columns.length + 2} className="text-center py-8 text-slate-500">Aucun élément trouvé</td></tr>
            ) : filteredItems.map(item => (
              <tr key={item.id} className="hover:bg-slate-50">
                {columns.map(col => (
                  <td key={col.key} className="px-4 py-3 text-sm">
                    {editingId === item.id ? (
                      col.type === 'select' ? (
                        <select
                          className="w-full border-slate-300 rounded text-sm py-1"
                          value={editingData[col.key] || ''}
                          onChange={e => setEditingData({ ...editingData, [col.key]: e.target.value })}
                        >
                          <option value="">--</option>
                          {col.options?.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                        </select>
                      ) : col.editable === false ? (
                        <span className="text-slate-700">{item[col.key]}</span>
                      ) : (
                        <input
                          className="w-full border-slate-300 rounded text-sm py-1 px-2"
                          value={editingData[col.key] || ''}
                          onChange={e => setEditingData({ ...editingData, [col.key]: e.target.value })}
                        />
                      )
                    ) : (
                      col.type === 'select' ? (
                        <Badge color="blue">{col.options?.find(o => o.value === item[col.key])?.label || item[col.key]}</Badge>
                      ) : (
                        <span className="text-slate-700">{item[col.key] ?? ''}</span>
                      )
                    )}
                  </td>
                ))}
                <td className="px-4 py-3 text-center">
                  <button onClick={() => toggleActive(item)} title={item.is_active ? 'Désactiver' : 'Activer'}>
                    {item.is_active
                      ? <ToggleRight className="w-5 h-5 text-green-600" />
                      : <ToggleLeft className="w-5 h-5 text-slate-400" />}
                  </button>
                </td>
                <td className="px-4 py-3 text-right space-x-2">
                  {editingId === item.id ? (
                    <>
                      <button onClick={saveEdit} disabled={savingId === item.id} className="text-green-600 hover:text-green-800">
                        {savingId === item.id ? <Loader2 className="w-4 h-4 animate-spin inline" /> : <CheckCircle className="w-4 h-4 inline" />}
                      </button>
                      <button onClick={cancelEdit} className="text-slate-400 hover:text-slate-600">
                        <X className="w-4 h-4 inline" />
                      </button>
                    </>
                  ) : (
                    <>
                      <button onClick={() => startEdit(item)} className="text-slate-400 hover:text-blue-600">
                        <Edit className="w-4 h-4 inline" />
                      </button>
                      <button onClick={() => setDeleteConfirmItem(item)} className="text-slate-400 hover:text-red-600">
                        <Trash2 className="w-4 h-4 inline" />
                      </button>
                    </>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <ConfirmationDialog
        isOpen={!!deleteConfirmItem}
        onClose={() => setDeleteConfirmItem(null)}
        onConfirm={handleDeleteConfirm}
        title="Confirmer la suppression"
        message={`Supprimer "${deleteConfirmItem?.[nameField] || deleteConfirmItem?.nom || deleteConfirmItem?.code || ''}" ? Cette action est irréversible.`}
        confirmLabel="Supprimer"
        variant="danger"
      />
    </div>
  );
};

const REFERENTIEL_SUBTABS = [
  {
    id: 'categories',
    label: 'Catégories',
    endpoint: '/categories',
    nameField: 'libelle',
    columns: [
      { key: 'libelle', label: 'Libellé', required: true },
      { key: 'description', label: 'Description' },
    ] as ColumnDef[],
  },
  {
    id: 'product-types',
    label: 'Types Produit',
    endpoint: '/product-types',
    nameField: 'libelle',
    columns: [
      { key: 'libelle', label: 'Libellé', required: true },
      { key: 'base_type', label: 'Type Base', type: 'select' as const, options: BASE_TYPE_OPTIONS, required: true },
      { key: 'description', label: 'Description' },
    ] as ColumnDef[],
  },
  {
    id: 'provenances',
    label: 'Provenances',
    endpoint: '/provenances',
    nameField: 'libelle',
    columns: [
      { key: 'libelle', label: 'Libellé', required: true },
      { key: 'description', label: 'Description' },
    ] as ColumnDef[],
  },
  {
    id: 'sites',
    label: 'Sites Stock',
    endpoint: '/sites',
    nameField: 'nom',
    fetchParams: { active_only: false },
    columns: [
      { key: 'code', label: 'Code', required: true },
      { key: 'nom', label: 'Nom', required: true },
      { key: 'adresse', label: 'Adresse' },
      { key: 'ville', label: 'Ville' },
      { key: 'telephone', label: 'Téléphone' },
      { key: 'responsable', label: 'Responsable' },
    ] as ColumnDef[],
  },
];

const ReferentielTab = () => {
  const [activeSubTab, setActiveSubTab] = useState(REFERENTIEL_SUBTABS[0].id);
  const currentSubTab = REFERENTIEL_SUBTABS.find(t => t.id === activeSubTab) || REFERENTIEL_SUBTABS[0];

  return (
    <div className="space-y-6 animate-in fade-in">
      {/* Sub-tabs */}
      <div className="flex gap-2 flex-wrap">
        {REFERENTIEL_SUBTABS.map(tab => (
          <button
            key={tab.id}
            onClick={() => setActiveSubTab(tab.id)}
            className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
              activeSubTab === tab.id
                ? 'bg-primary-100 text-primary-700 border border-primary-200'
                : 'bg-slate-100 text-slate-600 hover:bg-slate-200 border border-transparent'
            }`}
          >
            {tab.label}
          </button>
        ))}
      </div>

      <ReferenceDataTable
        key={currentSubTab.id}
        title={currentSubTab.label}
        endpoint={currentSubTab.endpoint}
        columns={currentSubTab.columns}
        nameField={currentSubTab.nameField}
        fetchParams={(currentSubTab as any).fetchParams}
      />
    </div>
  );
};

// --- Audit Tab ---

interface AuditLog {
  id: string;
  timestamp: string;
  user_id: string;
  user: string;
  action: string;
  entity_type: string;
  details: string;
}

const ACTION_LABELS: Record<string, string> = {
  CREATE: 'Création', UPDATE: 'Modification', DELETE: 'Suppression', SOFT_DELETE: 'Suppression',
  VALIDATE: 'Validation', CANCEL: 'Annulation', APPROVE: 'Approbation', REJECT: 'Rejet',
  STOCK_IN: 'Entrée stock', STOCK_OUT: 'Sortie stock', STOCK_TRANSFER: 'Transfert stock', STOCK_ADJUSTMENT: 'Ajustement stock',
  STATUS_CHANGE: 'Changement statut', CONTRACT_SIGN: 'Signature contrat', CONTRACT_ACTIVATE: 'Activation contrat', CONTRACT_CLOSE: 'Clôture contrat',
  PAYMENT_RECORD: 'Paiement enregistré', PAYMENT_VALIDATE: 'Paiement validé',
  CAUTION_PAYMENT: 'Paiement caution', CAUTION_REFUND: 'Remboursement caution',
  LOGIN: 'Connexion', LOGOUT: 'Déconnexion', LOGIN_FAILED: 'Échec connexion',
};

const ACTION_COLORS: Record<string, string> = {
  CREATE: 'bg-green-100 text-green-800', UPDATE: 'bg-blue-100 text-blue-800', DELETE: 'bg-red-100 text-red-800', SOFT_DELETE: 'bg-red-100 text-red-800',
  VALIDATE: 'bg-emerald-100 text-emerald-800', APPROVE: 'bg-emerald-100 text-emerald-800', REJECT: 'bg-red-100 text-red-800', CANCEL: 'bg-orange-100 text-orange-800',
  STOCK_IN: 'bg-green-100 text-green-800', STOCK_OUT: 'bg-orange-100 text-orange-800', STOCK_TRANSFER: 'bg-purple-100 text-purple-800', STOCK_ADJUSTMENT: 'bg-yellow-100 text-yellow-800',
  LOGIN: 'bg-slate-100 text-slate-700', LOGOUT: 'bg-slate-100 text-slate-700', LOGIN_FAILED: 'bg-red-100 text-red-800',
};

const ENTITY_LABELS: Record<string, string> = {
  MouvementStock: 'Mouvement Stock', ContratLocation: 'Contrat Location', ContratLOA: 'Contrat LOA',
  Facture: 'Facture', Devis: 'Devis', Client: 'Client', User: 'Utilisateur', Paiement: 'Paiement',
  BonLivraison: 'Bon de Livraison', CompanySettings: 'Paramètres', Product: 'Produit',
};

const AuditTab = () => {
  const [logs, setLogs] = useState<AuditLog[]>([]);
  const [users, setUsers] = useState<{ id: string; email: string; name: string }[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const PAGE_SIZE = 50;

  // Filters
  const [filterUser, setFilterUser] = useState('');
  const [filterAction, setFilterAction] = useState('');
  const [filterEntity, setFilterEntity] = useState('');
  const [filterDateFrom, setFilterDateFrom] = useState('');
  const [filterDateTo, setFilterDateTo] = useState('');

  // Fetch users for filter dropdown
  useEffect(() => {
    const fetchUsers = async () => {
      try {
        const response = await apiClient.get('/users', { params: { page_size: 100 } });
        const data = response.data?.data || [];
        setUsers(data.map((u: any) => ({ id: u.id, email: u.email, name: u.full_name || u.email })));
      } catch { /* ignore */ }
    };
    fetchUsers();
  }, []);

  // Fetch audit logs with filters
  const fetchLogs = useCallback(async () => {
    setIsLoading(true);
    try {
      const params: any = { page, page_size: PAGE_SIZE };
      if (filterUser) params.user_id = filterUser;
      if (filterAction) params.action = filterAction;
      if (filterEntity) params.entity_type = filterEntity;
      if (filterDateFrom) params.date_from = filterDateFrom;
      if (filterDateTo) params.date_to = filterDateTo;

      const response = await apiClient.get('/admin/audit', { params });
      const body = response.data;
      const logsData = Array.isArray(body?.data) ? body.data : (body?.data?.items || []);
      setLogs(logsData.map((l: any) => ({
        id: l.id,
        timestamp: l.created_at || l.timestamp,
        user_id: l.user_id || '',
        user: l.user_nom || l.user_email || 'Système',
        action: l.action || l.type,
        entity_type: l.entity_type || '',
        details: l.details || l.description || '',
      })));
      const pagination = body?.pagination;
      if (pagination) {
        setTotalPages(pagination.total_pages || 1);
        setTotalItems(pagination.total_items || logsData.length);
      }
    } catch (error) {
      console.error('Error fetching audit logs:', error);
    } finally {
      setIsLoading(false);
    }
  }, [page, filterUser, filterAction, filterEntity, filterDateFrom, filterDateTo]);

  useEffect(() => { fetchLogs(); }, [fetchLogs]);

  // Reset page when filters change
  const handleFilterChange = (setter: (v: string) => void) => (val: string) => { setter(val); setPage(1); };

  // Distinct action types from known enum
  const actionOptions = Object.keys(ACTION_LABELS);
  const entityOptions = Object.keys(ENTITY_LABELS);

  return (
    <div className="space-y-4 animate-in fade-in">
       {/* Header */}
       <div className="flex justify-between items-center">
          <p className="text-sm text-slate-500">{totalItems} entrée{totalItems > 1 ? 's' : ''}</p>
          <div className="flex items-center text-xs text-slate-500 bg-slate-100 px-3 py-1 rounded-full border border-slate-200">
             <Lock className="w-3 h-3 mr-1" /> Journal Immuable (Lecture Seule)
          </div>
       </div>

       {/* Filters */}
       <div className="bg-white p-4 rounded-lg border border-slate-200 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-3">
          <div>
            <label className="block text-xs font-medium text-slate-500 mb-1">Utilisateur</label>
            <select className="w-full border-slate-300 rounded-md text-sm py-1.5" value={filterUser} onChange={e => handleFilterChange(setFilterUser)(e.target.value)}>
              <option value="">Tous</option>
              {users.map(u => <option key={u.id} value={u.id}>{u.name}</option>)}
            </select>
          </div>
          <div>
            <label className="block text-xs font-medium text-slate-500 mb-1">Action</label>
            <select className="w-full border-slate-300 rounded-md text-sm py-1.5" value={filterAction} onChange={e => handleFilterChange(setFilterAction)(e.target.value)}>
              <option value="">Toutes</option>
              {actionOptions.map(a => <option key={a} value={a}>{ACTION_LABELS[a]}</option>)}
            </select>
          </div>
          <div>
            <label className="block text-xs font-medium text-slate-500 mb-1">Entité</label>
            <select className="w-full border-slate-300 rounded-md text-sm py-1.5" value={filterEntity} onChange={e => handleFilterChange(setFilterEntity)(e.target.value)}>
              <option value="">Toutes</option>
              {entityOptions.map(e => <option key={e} value={e}>{ENTITY_LABELS[e]}</option>)}
            </select>
          </div>
          <div>
            <label className="block text-xs font-medium text-slate-500 mb-1">Date début</label>
            <input type="date" className="w-full border-slate-300 rounded-md text-sm py-1.5" value={filterDateFrom} onChange={e => handleFilterChange(setFilterDateFrom)(e.target.value)} />
          </div>
          <div>
            <label className="block text-xs font-medium text-slate-500 mb-1">Date fin</label>
            <input type="date" className="w-full border-slate-300 rounded-md text-sm py-1.5" value={filterDateTo} onChange={e => handleFilterChange(setFilterDateTo)(e.target.value)} />
          </div>
       </div>

       {/* Table */}
       {isLoading ? (
         <div className="flex justify-center py-12"><Loader2 className="w-8 h-8 animate-spin text-primary-500" /></div>
       ) : (
       <div className="bg-white border border-slate-200 rounded-lg overflow-hidden">
          <table className="min-w-full divide-y divide-slate-200">
            <thead className="bg-slate-50">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase">Date</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase">Utilisateur</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase">Action</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase">Entité</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase">Description</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-200">
              {logs.map(log => (
                <tr key={log.id} className="hover:bg-slate-50">
                  <td className="px-4 py-3 text-xs text-slate-500 font-mono whitespace-nowrap">
                    {new Date(log.timestamp).toLocaleDateString('fr-FR')}{' '}
                    <span className="text-slate-400">{new Date(log.timestamp).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })}</span>
                  </td>
                  <td className="px-4 py-3 text-sm font-medium text-slate-800 whitespace-nowrap">{log.user}</td>
                  <td className="px-4 py-3">
                    <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${ACTION_COLORS[log.action] || 'bg-slate-100 text-slate-700'}`}>
                      {ACTION_LABELS[log.action] || log.action}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-xs text-slate-600">{ENTITY_LABELS[log.entity_type] || log.entity_type}</td>
                  <td className="px-4 py-3 text-sm text-slate-600 max-w-md truncate">{log.details}</td>
                </tr>
              ))}
              {logs.length === 0 && (
                <tr><td colSpan={5} className="text-center py-8 text-slate-500">Aucune entrée trouvée</td></tr>
              )}
            </tbody>
          </table>
          {/* Pagination */}
          {totalPages > 1 && (
            <div className="px-4 py-3 flex items-center justify-between border-t border-slate-200 bg-slate-50">
              <span className="text-sm text-slate-500">{totalItems} entrée{totalItems > 1 ? 's' : ''} — Page {page}/{totalPages}</span>
              <div className="flex gap-2">
                <Button size="sm" variant="ghost" disabled={page <= 1} onClick={() => setPage(p => p - 1)}>Précédent</Button>
                <Button size="sm" variant="ghost" disabled={page >= totalPages} onClick={() => setPage(p => p + 1)}>Suivant</Button>
              </div>
            </div>
          )}
       </div>
       )}
    </div>
  );
};

// --- Main Module ---

type AdminTab = 'users' | 'roles' | 'settings' | 'referentiel' | 'audit';

const ALL_TABS: { id: AdminTab; label: string; icon: any; adminOnly?: boolean }[] = [
  { id: 'users', label: 'Utilisateurs', icon: Users, adminOnly: true },
  { id: 'roles', label: 'Rôles & Permissions', icon: Shield, adminOnly: true },
  { id: 'settings', label: 'Paramètres', icon: Settings, adminOnly: true },
  { id: 'referentiel', label: 'Référentiel', icon: Database, adminOnly: true },
  { id: 'audit', label: "Journal d'Audit", icon: Activity },
];

export const AdminModule = ({ canWrite = true, userRole }: { canWrite?: boolean; userRole?: string }) => {
  const isAdmin = userRole === 'ADMIN';
  const visibleTabs = ALL_TABS.filter(t => !t.adminOnly || isAdmin);
  const [activeTab, setActiveTab] = useState<AdminTab>(isAdmin ? 'users' : 'audit');

  return (
    <div className="space-y-6">
       <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
           <h2 className="text-2xl font-bold text-slate-800">{isAdmin ? 'Administration Système' : "Journal d'Audit"}</h2>
           <p className="text-sm text-slate-500">{isAdmin ? 'Gestion des utilisateurs, sécurité et configuration globale.' : 'Historique des actions et opérations sur le système.'}</p>
        </div>
      </div>

      {/* Tabs */}
      {visibleTabs.length > 1 && (
      <div className="border-b border-slate-200">
        <nav className="-mb-px flex space-x-8 overflow-x-auto">
          {visibleTabs.map((tab) => {
             const Icon = tab.icon;
             return (
               <button
                 key={tab.id}
                 onClick={() => setActiveTab(tab.id)}
                 className={`group inline-flex items-center py-4 px-1 border-b-2 font-medium text-sm transition-colors whitespace-nowrap ${
                   activeTab === tab.id
                     ? 'border-primary-500 text-primary-600'
                     : 'border-transparent text-slate-500 hover:text-slate-700 hover:border-slate-300'
                 }`}
               >
                 <Icon className={`-ml-0.5 mr-2 h-5 w-5 ${activeTab === tab.id ? 'text-primary-500' : 'text-slate-400 group-hover:text-slate-500'}`} />
                 {tab.label}
               </button>
             );
          })}
        </nav>
      </div>
      )}

      {/* Content */}
      <div className="min-h-[500px]">
         {activeTab === 'users' && isAdmin && <UsersTab />}
         {activeTab === 'roles' && isAdmin && <RolesTab />}
         {activeTab === 'settings' && isAdmin && <SettingsTab />}
         {activeTab === 'referentiel' && isAdmin && <ReferentielTab />}
         {activeTab === 'audit' && <AuditTab />}
      </div>
    </div>
  );
};
